/**
 * Единая семантическая модель задач workspace (категории + финансовая релевантность).
 * Используется дашбордом, компактными очередями и другими клиентскими селекторами без дублирования ad-hoc строк.
 */
import { TASK_REGISTRY } from '../constants/taskRegistry';
import type { WorkspaceTask } from '../store/api/workspaceApi';

const FINANCE_EXPLICIT_TYPES = new Set<string>([
  'EXPENSE_CONFIRMATION',
  'PAYMENT_EXPECTED',
  'CLIENT_PAYMENT_DUE',
  'CREATE_CLIENT_INVOICE',
  'CARRIER_INVOICE_CONFIRM',
  'UPD_UPLOAD',
  'UPD_CONFIRM',
  'UPLOAD_UPD',
  'TN_ORIGINAL_MISSING',
  'UPLOAD_TN_ORIGINAL',
  'CONFIRM_INVOICE_SENT',
  'CONFIRM_UPD_SENT',
  'SEND_ORIGINAL_TN',
]);

export type TaskSemanticCategory =
  | 'OPERATIONAL'
  | 'FINANCIAL'
  | 'DOCUMENT'
  | 'CLOSURE'
  | 'BLOCKING'
  | 'INFORMATIONAL';

export type WorkspaceTaskSemantics = {
  /** Главная категория для UX-группировки */
  primary: TaskSemanticCategory;
  /** Попадает ли в финансовые очереди дашборда */
  financeRelevant: boolean;
  /** Документооборот (может пересекаться с FINANCIAL) */
  documentRelevant: boolean;
  /** Логистика / рейс без финансового акцента */
  operationalRelevant: boolean;
};

function taskTypeUpper(task: WorkspaceTask): string {
  return String(task.taskType || '').toUpperCase();
}

function isDocumentHeavyType(tt: string): boolean {
  if (tt.includes('DOCUMENT') || tt.includes('PHOTO') || tt.includes('UPD') || tt.includes('TN')) return true;
  if (tt.includes('ORIGINAL') || tt.includes('DOCS')) return true;
  return false;
}

/** Inbox V2: категория «что сделать» (не приоритет). */
export type InboxAttentionCategory = 'documents' | 'finance' | 'operations';

const INBOX_DOCUMENT_TASK_TYPES = new Set<string>([
  'LOADING_PHOTO',
  'UPLOAD_LOADING_PHOTO',
  'UNLOADING_PHOTO',
  'LOADING_DOCUMENTS_UPLOAD',
  'UNLOADING_DOCUMENTS_UPLOAD',
  'NO_DOCS_UNLOADING',
  'UPLOAD_LOADING_DOCS',
  'UPLOAD_UNLOADING_DOCS',
  'TN_ORIGINAL_MISSING',
  'UPLOAD_TN_ORIGINAL',
  'CARRIER_INVOICE_MISSING',
  'INVOICE_EXPECTED',
  'UPD_UPLOAD',
  'UPD_CONFIRM',
  'UPLOAD_UPD',
  'SEND_ORIGINAL_TN',
  'ORIGINALS_SENT',
]);

/**
 * Классификация задачи в корзину Inbox V2 (DESIGN-001).
 * Finance vs document: явная матрица; UPD → documents, CREATE_CLIENT_INVOICE → finance.
 */
export function resolveInboxCategory(task: WorkspaceTask): InboxAttentionCategory {
  const tt = taskTypeUpper(task);
  if (INBOX_DOCUMENT_TASK_TYPES.has(tt)) return 'documents';
  if (isFinanceWorkspaceTask(task)) return 'finance';
  if (isDocumentHeavyType(tt)) return 'documents';
  return 'operations';
}

/**
 * Финансовые задачи workspace: тот же контракт, что ранее в `financeWorkspaceTasks.ts`
 * (счёт/обязательство, openFinance, явные типы, оплаты/инвойсы/сверки).
 */
export function isFinanceWorkspaceTask(t: WorkspaceTask): boolean {
  if (t.invoiceId != null || t.obligationId != null) return true;

  const tt = taskTypeUpper(t);
  const meta = TASK_REGISTRY[tt];
  if (meta?.action === 'openFinance' || meta?.action === 'sendInvoiceToClient') return true;
  if (FINANCE_EXPLICIT_TYPES.has(tt)) return true;

  if (tt.includes('OBLIGATION')) return true;
  if (tt.includes('PAYMENT')) return true;
  if (tt.includes('RECONCIL')) return true;
  if (tt.includes('INVOICE')) {
    if (tt === 'CARRIER_INVOICE_MISSING' || tt === 'INVOICE_EXPECTED') return false;
    return true;
  }

  return false;
}

export function getWorkspaceTaskSemantics(task: WorkspaceTask): WorkspaceTaskSemantics {
  const tt = taskTypeUpper(task);
  const status = String(task.status || '').toUpperCase();
  const overdue = status === 'OVERDUE';

  const financeRelevant = isFinanceWorkspaceTask(task);
  const documentRelevant = isDocumentHeavyType(tt) || financeRelevant;
  const operationalRelevant =
    !financeRelevant && (TASK_REGISTRY[tt]?.role?.includes('logist') || TASK_REGISTRY[tt]?.action === 'openDispatch');

  let primary: TaskSemanticCategory = 'OPERATIONAL';
  if (financeRelevant) primary = 'FINANCIAL';
  else if (isDocumentHeavyType(tt)) primary = 'DOCUMENT';

  if (overdue && (financeRelevant || tt.includes('PAYMENT'))) {
    return { primary: 'BLOCKING', financeRelevant, documentRelevant, operationalRelevant };
  }

  if (status === 'INFO' || tt.includes('NOTIFY')) {
    return { primary: 'INFORMATIONAL', financeRelevant, documentRelevant, operationalRelevant };
  }

  return { primary, financeRelevant, documentRelevant, operationalRelevant };
}
